import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { format, parseISO } from 'date-fns';
import {
  Loader2, Bold, Italic, List, Paperclip, AtSign, Send, Pencil, Trash2, X, Check, MessageSquare,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import UserAvatar from './UserAvatar';
import { linkifyHtml } from '../utils/linkify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// FastAPI validation errors return `detail` as an array of objects —
// rendering that directly as a toast child crashes React.
const errorMessage = (e, fallback) => {
  const detail = e.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg).filter(Boolean).join(', ') || fallback;
  return fallback;
};

// role_level → compact badge (label + tone)
const ROLE_BADGE = {
  Manager:          { label: 'MANAGER',   cls: 'bg-blue-100 text-blue-700' },
  Admin:            { label: 'ADMIN',     cls: 'bg-red-100 text-red-700' },
  Developer:        { label: 'DEVELOPER', cls: 'bg-emerald-100 text-emerald-700' },
  'UI/UX Designer': { label: 'UI/UX',     cls: 'bg-purple-100 text-purple-700' },
  'Creative Head':  { label: 'CREATIVE',  cls: 'bg-pink-100 text-pink-700' },
  'Team Leader':    { label: 'TEAM LEAD', cls: 'bg-amber-100 text-amber-700' },
  'Team Member':    { label: 'SEO',       cls: 'bg-green-100 text-green-700' },
  'Read-only':      { label: 'READ-ONLY', cls: 'bg-slate-100 text-slate-600' },
};

const SOURCE_LABEL = {
  developer_note: 'Developer Note',
  uiux_note: 'UI/UX Note',
  manager_note: 'Manager Note',
  task_note: 'Task Note',
};

const renderTime = (iso) => {
  try { return format(parseISO(iso), 'MMM d, yyyy • h:mm a'); } catch { return iso || ''; }
};

const sanitize = (html) => DOMPurify.sanitize(html || '', {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'ul', 'ol', 'li', 'br', 'p', 'span', 'a'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
});

// Read-only rendering only — linkifies raw URL text into clickable <a>
// tags. Never used for the edit box, so a saved comment's stored content
// is never mutated with injected markup (no risk of double-linkifying on
// repeated edits).
const sanitizeDisplay = (html) => DOMPurify.sanitize(linkifyHtml(html || ''), {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'ul', 'ol', 'li', 'br', 'p', 'span', 'a'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
});

const RoleBadge = ({ role }) => {
  const cfg = ROLE_BADGE[role];
  if (!cfg) return null;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

const TaskComments = ({ taskId, onCountChange, onLinksAdded }) => {
  const { token, user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const editorRef = useRef(null);
  const editEditorRef = useRef(null);

  const headers = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const isModerator = user?.role_level === 'Manager' || user?.role_level === 'Admin';

  const load = async () => {
    if (!taskId) return;
    try {
      const res = await axios.get(`${API}/tasks/${taskId}/comments`, headers);
      const list = res.data?.comments || [];
      setComments(list);
      onCountChange?.(list.length);
    } catch (e) {
      // keep silent in the panel; surface via empty state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
  }, [taskId]);

  const exec = (cmd, ref) => {
    ref.current?.focus();
    document.execCommand(cmd, false, null);
  };

  const handlePost = async () => {
    const html = editorRef.current?.innerHTML?.trim() || '';
    const text = editorRef.current?.innerText?.trim() || '';
    if (!text) {
      toast.error('Comment cannot be empty');
      return;
    }
    setPosting(true);
    try {
      const res = await axios.post(`${API}/tasks/${taskId}/comments`, { content: html }, headers);
      if (editorRef.current) editorRef.current.innerHTML = '';
      await load();
      const added = res.data?.auto_added_links || [];
      if (added.length > 0) {
        onLinksAdded?.();
        toast.success(`Comment posted • ${added.length} link${added.length > 1 ? 's' : ''} added to Links`);
      } else {
        toast.success('Comment posted');
      }
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to post comment'));
    } finally {
      setPosting(false);
    }
  };

  const handleSaveEdit = async (id) => {
    const html = editEditorRef.current?.innerHTML?.trim() || '';
    const text = editEditorRef.current?.innerText?.trim() || '';
    if (!text) { toast.error('Comment cannot be empty'); return; }
    try {
      await axios.put(`${API}/tasks/${taskId}/comments/${id}`, { content: html }, headers);
      setEditingId(null);
      await load();
      toast.success('Comment updated');
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to update comment'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}/comments/${id}`, headers);
      await load();
      toast.success('Comment deleted');
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to delete comment'));
    }
  };

  const Toolbar = ({ targetRef }) => (
    <div className="flex items-center gap-1 px-2 py-1 border-t border-slate-100">
      <button type="button" onClick={() => exec('bold', targetRef)} title="Bold" className="p-1.5 rounded hover:bg-slate-100 text-slate-600" data-testid="comment-bold"><Bold size={14} /></button>
      <button type="button" onClick={() => exec('italic', targetRef)} title="Italic" className="p-1.5 rounded hover:bg-slate-100 text-slate-600" data-testid="comment-italic"><Italic size={14} /></button>
      <button type="button" onClick={() => exec('insertUnorderedList', targetRef)} title="Bullet list" className="p-1.5 rounded hover:bg-slate-100 text-slate-600" data-testid="comment-list"><List size={14} /></button>
      <span className="w-px h-4 bg-slate-200 mx-1" />
      <button type="button" disabled title="Attachments — coming soon" className="p-1.5 rounded text-slate-300 cursor-not-allowed"><Paperclip size={14} /></button>
      <button type="button" disabled title="@mentions — coming soon" className="p-1.5 rounded text-slate-300 cursor-not-allowed"><AtSign size={14} /></button>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="task-comments">
      {/* Thread */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-500" data-testid="comments-loading">
          <Loader2 className="animate-spin mr-2" size={16} /> Loading comments…
        </div>
      ) : comments.length === 0 ? (
        <div className="p-8 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 text-center" data-testid="comments-empty">
          <MessageSquare className="mx-auto text-slate-300 mb-2" size={28} />
          <p className="text-sm font-medium text-slate-600">No comments yet.</p>
          <p className="text-xs text-slate-400 mt-0.5">Start the discussion by posting the first comment.</p>
        </div>
      ) : (
        <ol className="space-y-3" data-testid="comments-feed">
          {comments.map((c) => {
            const canManage = c.user_id === user?.id || isModerator;
            const isEditing = editingId === c.id;
            const sourceLabel = SOURCE_LABEL[c.source_type];
            return (
              <li key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                <div className="flex-shrink-0 pt-0.5">
                  <UserAvatar user={{ full_name: c.user_name, profile_picture: c.profile_picture }} size="md" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800">{c.user_name || 'Unknown'}</span>
                    <RoleBadge role={c.user_role} />
                    <span className="text-[11px] text-slate-400 ml-auto whitespace-nowrap">{renderTime(c.created_at)}</span>
                  </div>
                  {sourceLabel && (
                    <div className="mt-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-cyan-700 bg-cyan-50 border border-cyan-100 px-1.5 py-0.5 rounded" data-testid={`comment-source-${c.source_type}`}>
                        Source: {sourceLabel}
                      </span>
                    </div>
                  )}
                  {isEditing ? (
                    <div className="mt-1.5 rounded-lg border border-cyan-300 bg-white">
                      <div
                        ref={editEditorRef}
                        contentEditable
                        suppressContentEditableWarning
                        className="px-3 py-2 text-sm text-slate-800 outline-none min-h-[48px] comment-editor"
                        dangerouslySetInnerHTML={{ __html: sanitize(c.content) }}
                        data-testid="comment-edit-input"
                      />
                      <div className="flex items-center justify-end gap-2 px-2 py-1.5 border-t border-slate-100">
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} data-testid="comment-edit-cancel"><X size={14} className="mr-1" />Cancel</Button>
                        <Button size="sm" onClick={() => handleSaveEdit(c.id)} data-testid="comment-edit-save"><Check size={14} className="mr-1" />Save</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="group mt-1 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                      <div
                        className="text-sm text-slate-700 leading-relaxed comment-content break-words"
                        dangerouslySetInnerHTML={{ __html: sanitizeDisplay(c.content) }}
                      />
                      <div className="flex items-center gap-3 mt-1">
                        {c.edited && <span className="text-[10px] text-slate-400 italic">edited</span>}
                        {canManage && (
                          <div className="flex items-center gap-2 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                            <button type="button" onClick={() => { setEditingId(c.id); }} className="text-slate-400 hover:text-cyan-600" title="Edit" data-testid={`comment-edit-${c.id}`}><Pencil size={13} /></button>
                            <button type="button" onClick={() => handleDelete(c.id)} className="text-slate-400 hover:text-red-600" title="Delete" data-testid={`comment-delete-${c.id}`}><Trash2 size={13} /></button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {/* Composer */}
      <div className="rounded-lg border border-slate-200 bg-white focus-within:border-cyan-400 transition-colors" data-testid="comment-composer">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          className="px-3 py-2.5 text-sm text-slate-800 outline-none min-h-[56px] comment-editor"
          data-placeholder="Add a comment..."
          data-testid="comment-input"
        />
        <div className="flex items-center justify-between">
          <Toolbar targetRef={editorRef} />
          <div className="px-2 py-1">
            <Button onClick={handlePost} disabled={posting} size="sm" className="btn-cta" data-testid="post-comment-button">
              {posting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
              Comment
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskComments;
